import React, { useMemo } from 'react';
import moment from 'moment';

const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

// Helper function to identify Outlook sync events
const isOutlookSyncEvent = (event) => {
  if (event.timecodeName === '(SYNC) Outlook Sync') return true;
  const idString = String(event.id || event.logId || '');
  if (idString.startsWith('crm:') || idString.startsWith('projects:crm:')) return true;
  return false;
};

export default function ListView({ 
  events, 
  currentDate, 
  onSelectEvent,
  onRightClickEvent,
  setupLongPress 
}) {
  
  // Filter events for the current month and sort them
  const monthEvents = useMemo(() => {
    const startOfMonth = moment(currentDate).startOf('month');
    const endOfMonth = moment(currentDate).endOf('month');
    
    return events
      .filter(event => {
        const eventDate = moment(event.start);
        return eventDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
      })
      .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
  }, [events, currentDate]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups = {};
    
    monthEvents.forEach(event => {
      const dateKey = moment(event.start).format('YYYY-MM-DD');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    
    return groups;
  }, [monthEvents]);

  // Calculate totals for the month
  const monthTotals = useMemo(() => {
    const totals = { total: 0, billable: 0 };
    
    monthEvents.forEach(event => {
      if (isOutlookSyncEvent(event)) return;
      
      const duration = moment(event.end).diff(moment(event.start), 'minutes') / 60;
      totals.total += duration;
      
      if (event.billingtype === 'Billable') {
        totals.billable += duration;
      }
    });
    
    return totals;
  }, [monthEvents]);

  const handleEventClick = (event, e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectEvent(event);
  };

  const handleEventRightClick = (event, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRightClickEvent) {
      onRightClickEvent(event, e);
    }
  };

  if (monthEvents.length === 0) {
    return (
      <div className="flex flex-col bg-gray-50 h-full min-h-[400px]">
        <div className="bg-white border-b border-gray-200 p-3">
          <div className="text-center">
            <h2 className="text-lg font-bold text-gray-800 mb-2">
              {moment(currentDate).format('MMMM YYYY')}
            </h2>
            <div className="flex justify-center space-x-4 text-sm">
              <div>
                <span className="text-gray-600">Total: </span>
                <span className="font-semibold text-gray-800">0:00h</span>
              </div>
              <div>
                <span className="text-gray-600">Billable: </span>
                <span className="font-semibold text-green-600">0:00h</span>
              </div>
              <div className="text-xs text-gray-500">
                (0 events)
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-gray-500">
          <div className="text-center">
            <h3 className="text-base font-semibold mb-2">No events for {moment(currentDate).format('MMMM YYYY')}</h3>
            <p className="text-sm">There are no registered events for this month.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 h-full min-h-[400px]">
      {/* Header with month and totals - more compact */}
      <div className="bg-white border-b border-gray-200 p-3">
        <div className="text-center">
          <h2 className="text-lg font-bold text-gray-800 mb-2">
            {moment(currentDate).format('MMMM YYYY')}
          </h2>
          <div className="flex justify-center space-x-4 text-sm">
            <div>
              <span className="text-gray-600">Total: </span>
              <span className="font-semibold text-gray-800">{formatHours(monthTotals.total)}</span>
            </div>
            <div>
              <span className="text-gray-600">Billable: </span>
              <span className="font-semibold text-green-600">{formatHours(monthTotals.billable)}</span>
            </div>
            <div className="text-xs text-gray-500">
              ({monthEvents.length} event{monthEvents.length !== 1 ? 's' : ''})
            </div>
          </div>
        </div>
      </div>

      {/* Events list - scrollable with fixed height */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {Object.entries(groupedEvents)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([dateKey, dayEvents]) => {
            const date = moment(dateKey);
            const dayTotals = dayEvents.reduce((acc, event) => {
              if (isOutlookSyncEvent(event)) return acc;
              
              const duration = moment(event.end).diff(moment(event.start), 'minutes') / 60;
              acc.total += duration;
              if (event.billingtype === 'Billable') {
                acc.billable += duration;
              }
              return acc;
            }, { total: 0, billable: 0 });

            return (
              <div key={dateKey} className="bg-white border-b border-gray-200">
                {/* Date header - more compact */}
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium text-gray-800 text-sm">
                        {date.format('dddd, D. MMMM')}
                      </h3>
                    </div>
                    <div className="text-xs text-gray-600">
                      <span className="mr-2">Total: {formatHours(dayTotals.total)}</span>
                      <span>Billable: {formatHours(dayTotals.billable)}</span>
                    </div>
                  </div>
                </div>

                {/* Events for this date */}
                <div className="divide-y divide-gray-100">
                  {dayEvents.map((event, index) => {
                    const duration = moment(event.end).diff(moment(event.start), 'minutes') / 60;
                    const startTime = moment(event.start).format('HH:mm');
                    const endTime = moment(event.end).format('HH:mm');

                    return (
                      <div
                        key={`${event.logId}-${index}`}
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={(e) => handleEventClick(event, e)}
                        onContextMenu={(e) => handleEventRightClick(event, e)}
                        {...(setupLongPress ? setupLongPress((e) => handleEventRightClick(event, e)) : {})}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center mb-1">
                              <h4 className="font-medium text-gray-900 truncate mr-2 text-sm">
                                {event.title || 'Untitled Event'}
                              </h4>
                              <img 
                                src={
                                  event.billingtype === 'Billable' 
                                    ? '/stateIcons/money.png'
                                    : event.billingtype === 'Pro Bono'
                                    ? '/stateIcons/sustainability.png'
                                    : '/stateIcons/workplace.png'
                                }
                                alt={event.billingtype}
                                className="w-4 h-4 flex-shrink-0"
                              />
                            </div>
                            
                            <div className="text-xs text-gray-600 space-y-0.5">
                              {event.projectName && (
                                <div className="flex items-center">
                                  <span className="font-medium mr-2">Project:</span>
                                  <span className="truncate">{event.projectName}</span>
                                </div>
                              )}
                              
                              {event.timecodeName && (
                                <div className="flex items-center">
                                  <span className="font-medium mr-2">Timecode:</span>
                                  <span className="truncate">{event.timecodeName}</span>
                                </div>
                              )}

                              {event.billingtype && (
                                <div className="flex items-center">
                                  <span className="font-medium mr-2">Type:</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    event.billingtype === 'Billable' 
                                      ? 'bg-green-100 text-green-800'
                                      : event.billingtype === 'Pro Bono'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {event.billingtype}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Time information moved to right side */}
                          <div className="flex-shrink-0 ml-4 text-right">                            
                            <div className="text-xs text-gray-800 font-medium">
                              {startTime} - {endTime}
                            </div>
                            <div className="text-xs text-gray-600">
                              ({formatHours(duration)})
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}